import React, { useState, useEffect } from 'react';
import { Layout, Input, Typography, Row, Col, Spin, Empty, message, Button } from 'antd';
import { useParams } from 'react-router-dom';
import { SearchOutlined, HomeOutlined } from '@ant-design/icons';
import { apiService } from '../../services/api';
import { AppListItem } from '../../types';
import AppCard from '../../components/AppCard/AppCard';
import Header from '../../components/Layout/Header';
import Footer from '../../components/Layout/Footer';
import Ad from '../../components/Ad/Ad';
import { constants } from '../../config/constants';
import './Search.css';

const { Content } = Layout;
const { Title } = Typography;

const Search: React.FC = () => {
  const { keyword: urlKeyword } = useParams<{ keyword: string }>();
  const [searchKeyword, setSearchKeyword] = useState<string>('');
  const [apps, setApps] = useState<AppListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  useEffect(() => {
    // Get search keyword from URL params
    if (urlKeyword) {
      const keyword = decodeURIComponent(urlKeyword);
      setSearchKeyword(keyword);
      performSearch(keyword);
    } else {
      // Reset state when no keyword in URL
      setSearchKeyword('');
      setApps([]);
      setHasSearched(false);
    }
  }, [urlKeyword]);

  const performSearch = async (keyword: string) => {
    if (!keyword.trim()) {
      setApps([]);
      setHasSearched(false);
      return;
    }

    try {
      setLoading(true);
      setHasSearched(true);
      const response = await apiService.getApps({
        page: 1,
        per_page: constants.pagination.defaultPageSize,
        search: keyword,
      });
      
      if (response.code === 0) {
        setApps(response.data.items);
      } else {
        message.error('Search failed');
        setApps([]);
      }
    } catch (error) {
      console.error('Search failed:', error);
      message.error('Search failed');
      setApps([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (value: string) => {
    const trimmedValue = value.trim();
    setSearchKeyword(trimmedValue);
    
    if (trimmedValue) {
      window.location.href = `/search/${encodeURIComponent(trimmedValue)}`;
    } else {
      window.location.href = '/search';
    }
  };



  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchKeyword(e.target.value);
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearch(searchKeyword);
    }
  };

  return (
    <Layout className="search-layout">
      <Header />
      <Content className="search-content">
        <div className="search-container">
          <div className="search-header">
            <div className="search-title-section">
              <Title level={2} className="page-title">
                <SearchOutlined /> Search Games
              </Title>
              <Button 
                type="text" 
                icon={<HomeOutlined />} 
                href="/"
                className="home-button"
              >
                Home
              </Button>
            </div>
            
            <div className="search-input-section">
              <Input
                size="large"
                placeholder="Search for games..."
                value={searchKeyword}
                onChange={handleInputChange}
                onKeyPress={handleKeyPress}
                suffix={
                  <SearchOutlined 
                    className="search-icon"
                    onClick={() => handleSearch(searchKeyword)}
                  />
                }
                className="main-search-input"
              />
            </div>
          </div>

          {/* 广告展示区域 - 在搜索框和检索结果之间 */}
          <div style={{ 
            marginTop: 24, 
            marginBottom: 24, 
            display: 'flex', 
            justifyContent: 'center', 
            width: '100%',
            minHeight: '100px'
          }}>
            <Ad positionName="search_page_ad" className="search-ad" />
          </div>

          <div className="search-results">
            {loading ? (
              <div className="loading-container">
                <Spin size="large" />
                <div className="loading-text">Searching...</div>
              </div>
            ) : hasSearched ? (
              apps.length > 0 ? (
                <>
                  <div className="results-header">
                    <Title level={4} className="results-title">
                      Found {apps.length} results for "{urlKeyword ? decodeURIComponent(urlKeyword) : ''}"
                    </Title>
                  </div>
                  <Row gutter={[16, 16]}>
                    {apps.map(app => (
                      <Col key={app.id} xs={24} sm={12} md={8} lg={6}>
                        <AppCard
                          app={app}
                        />
                      </Col>
                    ))}
                  </Row>
                </>
              ) : (
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={
                    <span>
                      No results found for "{urlKeyword ? decodeURIComponent(urlKeyword) : ''}"
                      <br />
                      Try different keywords
                    </span>
                  }
                  className="empty-results"
                />
              )
            ) : (
              <div className="search-placeholder">
                <SearchOutlined className="placeholder-icon" />
                <Title level={4} className="placeholder-title">
                  Search for Your Favorite Games
                </Title>
                <p className="placeholder-text">
                  Enter keywords to find games, apps, and more
                </p>
              </div>
            )}
          </div>
        </div>
      </Content>
      <Footer />
    </Layout>
  );
};

export default Search;
